/**
 * TeamsTab Component
 * Team management within Admin page
 */

import React, { useState } from 'react';
import { Plus, Edit, Trash2, UsersRound, Users } from 'lucide-react';
import { Card, Button, Input, Modal, LoadingSpinner, EmptyState } from '../../../components/common';
import { useTeams, useCreateTeam, useUpdateTeam, useDeleteTeam, Team } from '../../../hooks/useTeams';

export const TeamsTab: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');

  const { data: teams, isLoading } = useTeams();
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const deleteTeam = useDeleteTeam();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamName.trim()) return;

    if (editingTeam) {
      updateTeam.mutate(
        { teamId: editingTeam.id, name: teamName.trim(), description: teamDescription.trim() },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            setEditingTeam(null);
            setTeamName('');
            setTeamDescription('');
          },
        }
      );
    } else {
      createTeam.mutate(
        { name: teamName.trim(), description: teamDescription.trim() || undefined },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            setTeamName('');
            setTeamDescription('');
          },
        }
      );
    }
  };

  const handleEdit = (team: Team) => {
    setEditingTeam(team);
    setTeamName(team.name);
    setTeamDescription(team.description || '');
    setIsModalOpen(true);
  };

  const handleDelete = (team: Team) => {
    if (team.memberCount > 0) {
      if (!window.confirm(`This team has ${team.memberCount} members. All memberships will be removed. Continue?`)) {
        return;
      }
    } else if (!window.confirm(`Are you sure you want to delete "${team.name}"?`)) {
      return;
    }
    deleteTeam.mutate(team.id);
  };

  const openCreateModal = () => {
    setEditingTeam(null);
    setTeamName('');
    setTeamDescription('');
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Teams</h2>
          <p className="text-sm text-stone-500">
            Create cross-departmental teams and manage team memberships
          </p>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" />
          Add Team
        </Button>
      </div>

      {/* Teams List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !teams?.length ? (
        <EmptyState
          icon={UsersRound}
          title="No teams yet"
          description="Create teams to group members across departments"
          action={{
            label: 'Add Team',
            onClick: openCreateModal,
          }}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <Card key={team.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <UsersRound className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-stone-900">{team.name}</h3>
                    {team.description && (
                      <p className="text-xs text-stone-400 truncate max-w-[160px]">{team.description}</p>
                    )}
                    <div className="flex items-center gap-1 text-sm text-stone-500">
                      <Users className="w-3 h-3" />
                      <span>{team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(team)}
                    className="p-2 text-stone-400 hover:text-primary-600 rounded-lg hover:bg-stone-100"
                    title="Edit team"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(team)}
                    className="p-2 text-stone-400 hover:text-danger-600 rounded-lg hover:bg-stone-100"
                    title="Delete team"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTeam(null);
          setTeamName('');
          setTeamDescription('');
        }}
        title={editingTeam ? 'Edit Team' : 'Add Team'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="team-name" className="block text-sm font-medium text-stone-700 mb-1">
              Team Name
            </label>
            <Input
              id="team-name"
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="e.g., Platform Engineering, Growth Squad"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="team-description" className="block text-sm font-medium text-stone-700 mb-1">
              Description <span className="text-stone-400 font-normal">(optional)</span>
            </label>
            <Input
              id="team-description"
              type="text"
              value={teamDescription}
              onChange={(e) => setTeamDescription(e.target.value)}
              placeholder="Brief description of the team's purpose"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                setEditingTeam(null);
                setTeamName('');
                setTeamDescription('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!teamName.trim() || createTeam.isPending || updateTeam.isPending}
            >
              {createTeam.isPending || updateTeam.isPending ? (
                <LoadingSpinner size="sm" />
              ) : editingTeam ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
